import { useState, Fragment } from 'react';
import { Movie } from '../types/movie';
import { ChevronUp, ChevronDown, Heart, Star, Trash2, Edit, Check, X, ArrowUpDown, Calendar, User } from 'lucide-react';

interface MovieTableProps {
  movies: Movie[];
  onMovieClick?: (movie: Movie) => void;
  onDeleteMovie?: (movieId: number) => void;
  onUpdateScore?: (movieId: number, newScore: number) => void;
  isAuthenticated?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSortChange?: (field: string, order: 'asc' | 'desc') => void;
}

export const MovieTable: React.FC<MovieTableProps> = ({ 
  movies, 
  onMovieClick, 
  onDeleteMovie, 
  onUpdateScore, 
  isAuthenticated = false,
  sortBy = 'year',
  sortOrder = 'desc',
  onSortChange
}) => {
  const [expandedMovie, setExpandedMovie] = useState<number | null>(null);
  const [editingScore, setEditingScore] = useState<number | null>(null);
  const [tempScore, setTempScore] = useState<string>('');

  const handleSort = (field: string) => {
    if (!onSortChange) return;
    
    const newOrder = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc';
    onSortChange(field, newOrder);
  };

  const getSortIcon = (field: string) => {
    if (sortBy !== field) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortOrder === 'asc' 
      ? <ChevronUp className="h-4 w-4 text-primary-600" />
      : <ChevronDown className="h-4 w-4 text-primary-600" />;
  };

  const handleEditScore = (movie: Movie, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingScore(movie.id);
    setTempScore(movie.score.toString());
  };

  const handleSaveScore = (movieId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onUpdateScore) {
      const scoreValue = parseFloat(tempScore) || 0;
      onUpdateScore(movieId, scoreValue);
    }
    setEditingScore(null);
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingScore(null);
  };

  const handleDeleteMovie = (movieId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDeleteMovie && window.confirm('Are you sure you want to delete this movie?')) {
      onDeleteMovie(movieId);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 8) return 'text-green-600';
    if (score >= 7) return 'text-blue-600';
    if (score >= 6) return 'text-yellow-600';
    if (score >= 5) return 'text-orange-600';
    return 'text-red-600';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    
    // Check if it's an Excel serial number (entire string is a 5-digit number)
    if (/^\d{5}$/.test(dateString)) {
      const serialNumber = parseInt(dateString);
      
      // Convert Excel serial number to date
      // Excel serial numbers start from January 1, 1900 (serial 1)
      // Excel incorrectly treats 1900 as a leap year, so we need to handle this
      let date;
      if (serialNumber <= 60) {
        // For dates before March 1, 1900, Excel's leap year bug doesn't apply
        date = new Date(1900, 0, serialNumber - 1);
      } else {
        // For dates after March 1, 1900, we need to account for the leap year bug
        date = new Date(1900, 0, serialNumber - 2);
      }
      
      return date.toISOString().split('T')[0];
    }
    
    // Check if it's already a valid date string
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateString; // Already in YYYY-MM-DD format
    }
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return '-';
      }
      return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
    } catch (error) {
      return '-';
    }
  };

  // Mobile card view
  const renderMobileCard = (movie: Movie) => (
    <div 
      key={movie.id}
      className="bg-white rounded-lg border border-gray-200 p-4 mb-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => {
        if (onMovieClick) {
          onMovieClick(movie);
        } else {
          setExpandedMovie(expandedMovie === movie.id ? null : movie.id);
        }
      }}
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 truncate mb-1">
            {movie.title || 'Untitled'}
          </h3>
          <div className="flex items-center space-x-3 text-sm text-gray-600 mb-2">
            <span className="flex items-center space-x-1">
              <Calendar className="h-4 w-4" />
              <span>{movie.year}</span>
            </span>
            <span className="flex items-center space-x-1">
              <User className="h-4 w-4" />
              <span className="truncate max-w-24">{movie.director || 'Unknown'}</span>
            </span>
          </div>
        </div>
        <div className="flex items-center space-x-2 ml-3">
          {movie.gf && <Heart className="h-5 w-5 text-red-500" />}
          <div className="flex items-center space-x-1">
            <Star className={`h-4 w-4 ${getScoreColor(movie.score)}`} />
            <span className={`font-medium text-sm ${getScoreColor(movie.score)}`}>
              {movie.score > 0 ? movie.score.toFixed(1) : '-'}
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 mb-3">
        {movie.genres.slice(0, 3).map((genre, index) => (
          <span
            key={index}
            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
          >
            {genre}
          </span>
        ))}
        {movie.genres.length > 3 && (
          <span className="text-xs text-gray-400 px-2 py-1">+{movie.genres.length - 3}</span>
        )}
      </div>

      {isAuthenticated && (
        <div className="flex items-center justify-end space-x-2 pt-2 border-t border-gray-100">
          <button
            onClick={(e) => handleEditScore(movie, e)}
            className="text-primary-600 hover:text-primary-800 p-1"
            title="Edit score"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => handleDeleteMovie(movie.id, e)}
            className="text-red-600 hover:text-red-800 p-1"
            title="Delete movie"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )}

      {expandedMovie === movie.id && movie.plotSummary && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="text-sm text-gray-700">
            <strong>Plot Summary:</strong> {movie.plotSummary}
          </div>
        </div>
      )}
    </div>
  );

  // Desktop table view
  const renderDesktopTable = () => (
    <div className="table-container">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('year')}
              >
                <div className="flex items-center space-x-1">
                  <span>Year</span>
                  {getSortIcon('year')}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('title')}
              >
                <div className="flex items-center space-x-1">
                  <span>Title</span>
                  {getSortIcon('title')}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                Genres
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('director')}
              >
                <div className="flex items-center space-x-1">
                  <span>Director</span>
                  {getSortIcon('director')}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('score')}
              >
                <div className="flex items-center space-x-1">
                  <span>Score</span>
                  {getSortIcon('score')}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                GF
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('lastSeen')}
              >
                <div className="flex items-center space-x-1">
                  <span>Last Seen</span>
                  {getSortIcon('lastSeen')}
                </div>
              </th>
              {isAuthenticated && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {movies.map((movie) => (
              <Fragment key={movie.id}>
                <tr 
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => {
                    if (onMovieClick) {
                      onMovieClick(movie);
                    } else {
                      setExpandedMovie(expandedMovie === movie.id ? null : movie.id);
                    }
                  }}
                >
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {movie.year}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium truncate max-w-xs">{movie.title || 'Untitled'}</span>
                      {movie.plotSummary && (
                        <button className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                          {expandedMovie === movie.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-500">
                    <div className="flex flex-wrap gap-1 max-w-32">
                      {movie.genres.slice(0, 2).map((genre, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 truncate"
                        >
                          {genre}
                        </span>
                      ))}
                      {movie.genres.length > 2 && (
                        <span className="text-xs text-gray-400">+{movie.genres.length - 2}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-500">
                    <span className="truncate max-w-24 block">{movie.director || 'Unknown'}</span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    {editingScore === movie.id ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="0"
                          max="10"
                          step="0.1"
                          value={tempScore}
                          onChange={(e) => setTempScore(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <button
                          onClick={(e) => handleSaveScore(movie.id, e)}
                          className="text-green-600 hover:text-green-800"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-1">
                        <Star className={`h-4 w-4 ${getScoreColor(movie.score)}`} />
                        <span className={`font-medium ${getScoreColor(movie.score)}`}>
                          {movie.score > 0 ? movie.score.toFixed(1) : '-'}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {movie.gf ? (
                      <Heart className="h-5 w-5 text-red-500" />
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-700">
                    {formatDate(movie.lastSeen)}
                  </td>
                  {isAuthenticated && (
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => handleEditScore(movie, e)}
                          className="text-primary-600 hover:text-primary-800"
                          title="Edit score"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => handleDeleteMovie(movie.id, e)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete movie"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
                {expandedMovie === movie.id && movie.plotSummary && (
                  <tr>
                    <td colSpan={8} className="px-4 py-4 bg-gray-50">
                      <div className="text-sm text-gray-700">
                        <strong>Plot Summary:</strong> {movie.plotSummary}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div>
      {/* Mobile view */}
      <div className="block lg:hidden">
        {movies.map(renderMobileCard)}
      </div>

      {/* Desktop view */}
      <div className="hidden lg:block">
        {renderDesktopTable()}
      </div>
      
      {movies.length === 0 && (
        <div className="text-center py-8 sm:py-12">
          <div className="text-gray-500 text-base sm:text-lg">No movies found matching your criteria</div>
          <div className="text-gray-400 text-sm mt-2">Try adjusting your filters</div>
        </div>
      )}
    </div>
  );
};
